/**
 * Visualizador de Páginas — mostra a imagem de uma ou mais páginas do
 * balancete (as mesmas imagens usadas pelo OCR), com navegação, zoom,
 * "tamanho real" e atalho para abrir o PDF original.
 *
 * Reutilizado pelas três "portas de entrada":
 *   A) campo "digitar páginas" (2;7;9 ou 5-9)
 *   B) botão "abrir PDF original"
 *   C) clicar na linha de um relatório (🔎 pág. N)
 */
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileText,
  Loader2,
  Maximize2,
  Minus,
  Plus,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { paginaImgUrl, pdfUrl } from "../lib/api";

interface Props {
  competenciaId: number;
  /** Páginas a exibir, na ordem. Ex.: [2, 7, 9] ou [5,6,7,8,9]. */
  paginas: number[];
  aberto: boolean;
  onFechar: () => void;
  /** Posição inicial dentro de `paginas` (padrão 0). */
  indiceInicial?: number;
}

const ZOOM_MIN = 0.4;
const ZOOM_MAX = 3;
const ZOOM_PASSO = 0.2;

export function VisualizadorPaginas({
  competenciaId,
  paginas,
  aberto,
  onFechar,
  indiceInicial = 0,
}: Props) {
  const [idx, setIdx] = useState(indiceInicial);
  const [zoom, setZoom] = useState(1);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);

  // Reposiciona quando reabre ou muda o conjunto de páginas.
  useEffect(() => {
    if (aberto) {
      setIdx(Math.min(indiceInicial, Math.max(0, paginas.length - 1)));
      setZoom(1);
    }
  }, [aberto, indiceInicial, paginas]);

  const paginaAtual = paginas[idx];

  const anterior = useCallback(() => {
    setIdx((i) => Math.max(0, i - 1));
    setZoom(1);
  }, []);
  const proxima = useCallback(() => {
    setIdx((i) => Math.min(paginas.length - 1, i + 1));
    setZoom(1);
  }, [paginas.length]);

  // Teclado: Esc fecha, setas navegam.
  useEffect(() => {
    if (!aberto) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onFechar();
      else if (e.key === "ArrowLeft") anterior();
      else if (e.key === "ArrowRight") proxima();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [aberto, onFechar, anterior, proxima]);

  // Reseta estado de carregamento ao trocar de página.
  useEffect(() => {
    setCarregando(true);
    setErro(false);
  }, [paginaAtual]);

  if (!aberto || paginaAtual == null) return null;

  const temAnterior = idx > 0;
  const temProxima = idx < paginas.length - 1;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-slate-900/80 backdrop-blur-sm"
      onClick={onFechar}
    >
      {/* Barra superior */}
      <div
        className="flex items-center gap-3 px-4 py-2.5 bg-slate-800 text-white shadow"
        onClick={(e) => e.stopPropagation()}
      >
        <FileText size={18} className="text-slate-300" />
        <div className="text-sm font-medium">
          Página {paginaAtual}
          {paginas.length > 1 && (
            <span className="text-slate-400 font-normal">
              {"  "}({idx + 1} de {paginas.length})
            </span>
          )}
        </div>

        <div className="flex-1" />

        {/* Zoom */}
        <div className="flex items-center gap-1 mr-2">
          <button
            type="button"
            onClick={() => setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_PASSO))}
            className="p-1.5 rounded hover:bg-slate-700"
            title="Diminuir"
          >
            <Minus size={16} />
          </button>
          <span className="text-xs tabular-nums w-10 text-center">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            onClick={() => setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_PASSO))}
            className="p-1.5 rounded hover:bg-slate-700"
            title="Aumentar"
          >
            <Plus size={16} />
          </button>
        </div>

        {/* Tamanho real (PNG em nova aba) */}
        <a
          href={paginaImgUrl(competenciaId, paginaAtual)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded border border-slate-600 hover:bg-slate-700"
          title="Abrir a imagem em resolução máxima em nova aba"
        >
          <Maximize2 size={13} /> Tamanho real
        </a>

        {/* Abrir PDF original nesta página */}
        <a
          href={pdfUrl(competenciaId, paginaAtual)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded border border-slate-600 hover:bg-slate-700"
          title="Abrir o PDF original do balancete (para navegar/imprimir)"
        >
          <ExternalLink size={13} /> Abrir PDF
        </a>

        <button
          type="button"
          onClick={onFechar}
          className="p-1.5 rounded hover:bg-slate-700 ml-1"
          title="Fechar (Esc)"
        >
          <X size={18} />
        </button>
      </div>

      {/* Área da imagem */}
      <div
        className="relative flex-1 overflow-auto flex items-start justify-center p-4"
        onClick={(e) => e.stopPropagation()}
      >
        {carregando && !erro && (
          <div className="absolute inset-0 flex items-center justify-center text-slate-200">
            <Loader2 size={28} className="animate-spin" />
          </div>
        )}

        {erro ? (
          <div className="mt-16 max-w-md text-center text-slate-200">
            <p className="text-sm">
              Não foi possível carregar a página {paginaAtual}.
            </p>
            <p className="text-xs text-slate-400 mt-2">
              Isso acontece quando o mês ainda não foi processado (as imagens
              vêm do OCR) ou quando a página não existe neste balancete. Você
              ainda pode{" "}
              <a
                href={pdfUrl(competenciaId, paginaAtual)}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-white"
              >
                abrir o PDF original
              </a>
              .
            </p>
          </div>
        ) : (
          <img
            src={paginaImgUrl(competenciaId, paginaAtual, 1600)}
            alt={`Página ${paginaAtual} do balancete`}
            onLoad={() => setCarregando(false)}
            onError={() => {
              setCarregando(false);
              setErro(true);
            }}
            style={{ width: `${zoom * 100}%`, maxWidth: "none" }}
            className="rounded shadow-lg bg-white h-auto"
          />
        )}

        {/* Setas de navegação (quando há mais de uma página) */}
        {temAnterior && (
          <button
            type="button"
            onClick={anterior}
            className="fixed left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-slate-800/80 text-white hover:bg-slate-700"
            title="Página anterior (←)"
          >
            <ChevronLeft size={24} />
          </button>
        )}
        {temProxima && (
          <button
            type="button"
            onClick={proxima}
            className="fixed right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-slate-800/80 text-white hover:bg-slate-700"
            title="Próxima página (→)"
          >
            <ChevronRight size={24} />
          </button>
        )}
      </div>
    </div>
  );
}
